import { apiGet, apiPost, apiPut, setAuthToken, getAuthToken } from './apiClient';

let currentSession = null;
const listeners = new Set();

const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || '')
  .toLowerCase()
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

function normalizeRole(role) {
  const value = String(role || '').toLowerCase();
  if (['admin', 'administrator', 'superadmin', 'owner'].includes(value)) return 'admin';
  if (['hr', 'humanresources', 'human-resources'].includes(value)) return 'hr';
  if (['karyawan', 'employee', 'staff'].includes(value)) return 'karyawan';
  return 'user';
}

function normalizeEmployee(employee) {
  if (!employee) return null;
  return {
    ...employee,
    role: normalizeRole(employee.role),
  };
}

function buildAuthUser(session) {
  if (!session?.user) return null;
  const { user } = session;

  return {
    uid: user.uid,
    email: user.email,
    getIdToken: async (forceRefresh = false) => {
      if (forceRefresh) {
        await refreshSession();
      }
      return getAuthToken();
    },
    getIdTokenResult: async (forceRefresh = false) => {
      if (forceRefresh) {
        await refreshSession();
      }
      const sessionUser = currentSession?.user || user;
      return {
        token: getAuthToken(),
        claims: {
          role: sessionUser.role,
          companyId: sessionUser.companyId || 'default',
          admin: sessionUser.role === 'admin',
        },
      };
    },
  };
}

export const auth = {
  get currentUser() {
    return buildAuthUser(currentSession);
  },
};

function notifyListeners() {
  listeners.forEach((fn) => {
    try { fn(currentSession); } catch (error) { console.error(error); }
  });
}

async function refreshSession() {
  const token = getAuthToken();
  if (!token) {
    currentSession = null;
    return null;
  }
  const data = await apiGet('/auth/me');
  currentSession = {
    token,
    user: data.user,
    employee: normalizeEmployee(data.employee),
    mustChangePassword: data.mustChangePassword,
  };
  return currentSession;
}

async function applySession(session) {
  currentSession = session;
  if (session?.token) setAuthToken(session.token);
  else setAuthToken(null);
  notifyListeners();
  return currentSession;
}

async function applySessionFromLogin(data) {
  return applySession({
    token: data.token,
    user: data.user,
    employee: normalizeEmployee(data.employee),
    mustChangePassword: data.mustChangePassword,
  });
}

async function applySessionFromMe(data) {
  return applySession({
    token: getAuthToken(),
    user: data.user,
    employee: normalizeEmployee(data.employee),
    mustChangePassword: data.mustChangePassword,
  });
}

export function onAuthChange(callback) {
  listeners.add(callback);
  if (currentSession !== null) callback(currentSession);
  return () => listeners.delete(callback);
}

export async function initAuth() {
  const token = getAuthToken();
  if (!token) {
    currentSession = null;
    notifyListeners();
    return null;
  }

  try {
    const data = await apiGet('/auth/me');
    return applySessionFromMe(data);
  } catch (error) {
    if (error?.status !== 401) {
      console.error('Auth init failed:', error);
    }
    currentSession = null;
    setAuthToken(null);
    notifyListeners();
    return null;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', () => {
    currentSession = null;
    notifyListeners();
  });
}

export async function login(email, password) {
  const data = await apiPost('/auth/login', { email, password });
  return applySessionFromLogin(data);
}

export async function signInWithEmailAndPassword(_auth, email, password) {
  const session = await login(email, password);
  return { user: auth.currentUser, ...session };
}

export async function signOut() {
  return logout();
}

export async function logout() {
  currentSession = null;
  setAuthToken(null);
  notifyListeners();
}

export function getCurrentSession() {
  return currentSession;
}

export async function changePassword(currentPassword, newPassword) {
  await apiPost('/auth/change-password', { currentPassword, newPassword });
  if (currentSession) {
    currentSession.mustChangePassword = false;
    if (currentSession.employee) {
      currentSession.employee.firstLoginPasswordChangeRequired = false;
    }
    notifyListeners();
  }
  return { success: true };
}

export async function updatePassword(_auth, newPassword) {
  return changePassword(null, newPassword);
}

export async function registerEmployee(signupData) {
  const result = await apiPost('/auth/register', {
    ...signupData,
    email: String(signupData.email || '').toLowerCase(),
  });
  return { success: true, id: result.id, message: result.message };
}

export async function createUserWithEmailAndPassword(_auth, email, password) {
  const result = await apiPost('/auth/register', {
    email,
    password,
    fullName: email,
    isAdminSignup: true,
  });
  setAuthToken(result.token);
  const me = await apiGet('/auth/me');
  await applySessionFromMe(me);
  return { user: auth.currentUser, ...currentSession };
}

export async function ensureUserRoleClaim() {
  try {
    const res = await apiPost('/auth/ensure-admin-role', {});
    if (res.updated && currentSession?.user) {
      currentSession.user.role = res.role || 'admin';
      notifyListeners();
    }
    return res;
  } catch (error) {
    console.warn('ensureUserRoleClaim failed:', error);
    return { updated: false, role: currentSession?.user?.role || 'user' };
  }
}

export async function markFirstPasswordChanged(employeeId) {
  if (!employeeId) return;
  try {
    await apiPut(`/employees/${encodeURIComponent(employeeId)}`, {
      firstLoginPasswordChangeRequired: false,
    });
    if (currentSession?.employee?.id === employeeId) {
      currentSession.employee.firstLoginPasswordChangeRequired = false;
      currentSession.mustChangePassword = false;
      notifyListeners();
    }
  } catch (error) {
    console.warn('markFirstPasswordChanged failed:', error);
  }
}

export { getAuthToken };
